import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Barra de encabezado DENTRO de la página: migas, título de detalle y acciones.
 *
 * Ya NO recibe `title` ni `subtitle`. Desde el rediseño de set-2026 el título
 * de la pantalla y su subtítulo los dibuja el header de 70 px del Layout, con
 * los datos de `src/nav.ts` (que además sabe qué sub-pestaña está activa).
 * Cuando las páginas traían su propio `<PageHeader title=...>` el título
 * aparecía dos veces en la misma pantalla.
 *
 * `detailTitle` es para las pantallas que muestran UN registro concreto — el
 * detalle de una conversación, por ejemplo: ahí el header dice el módulo
 * ("Operación") y hace falta además el nombre del cliente. Es el token
 * "título de detalle" del diseño: 15 px / 800.
 */

interface BreadcrumbItem {
  label: string
  href?: string
}

interface PageHeaderProps {
  /** Nombre del registro que se está viendo. No es el título de la pantalla. */
  detailTitle?: string
  detailSubtitle?: string
  breadcrumbs?: BreadcrumbItem[]
  actions?: ReactNode
}

export default function PageHeader({
  detailTitle, detailSubtitle, breadcrumbs, actions,
}: PageHeaderProps) {
  const vacio = !detailTitle && !detailSubtitle && !breadcrumbs?.length && !actions
  if (vacio) return null

  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div className="min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mb-1 flex items-center gap-1 text-micro text-ink-muted">
            {breadcrumbs.map((crumb, idx) => (
              <span key={idx} className="flex items-center gap-1">
                {idx > 0 && <span className="opacity-40">/</span>}
                {crumb.href ? (
                  <Link to={crumb.href} className="hover:text-ink hover:underline">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-ink">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        {detailTitle && (
          <h2 className="text-[15px] font-extrabold text-ink">{detailTitle}</h2>
        )}
        {detailSubtitle && (
          <p className="mt-0.5 text-body text-ink-muted">{detailSubtitle}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
